import { useState } from 'react';
import { MessageSquare, Plus, Trash2, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import type { Conversation } from '../../types';

interface ConversationSidebarProps {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

export function ConversationSidebar({
  conversations,
  currentConversation,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  isCollapsed,
  onToggleCollapse,
  onMobileClose,
  isMobile = false,
}: ConversationSidebarProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filteredConversations = conversations.filter(conv =>
    conv.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group conversations by date
  const groupedConversations = filteredConversations.reduce((groups, conv) => {
    const date = new Date(conv.updated_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    let group: string;
    if (date.toDateString() === today.toDateString()) {
      group = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      group = 'Yesterday';
    } else if (date > lastWeek) {
      group = 'This Week';
    } else {
      group = 'Older';
    }

    if (!groups[group]) groups[group] = [];
    groups[group].push(conv);
    return groups;
  }, {} as Record<string, Conversation[]>);

  const groupOrder = ['Today', 'Yesterday', 'This Week', 'Older'];

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (deleteConfirm === id) {
      onDeleteConversation(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
      // Reset confirm state after 3 seconds
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  if (isCollapsed) {
    return (
      <div className="w-12 bg-gray-100 border-r border-gray-200 flex flex-col items-center py-4 flex-shrink-0 dark:bg-gray-800 dark:border-gray-700">
        <button
          onClick={onToggleCollapse}
          className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-lg mb-4 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700"
          title="Expand sidebar"
        >
          <ChevronRight size={20} />
        </button>
        <button
          onClick={onNewConversation}
          className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700"
          title="New conversation"
        >
          <Plus size={20} />
        </button>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? 'w-72 max-w-[85vw]' : 'w-64'} h-full bg-gray-100 border-r border-gray-200 flex flex-col flex-shrink-0 dark:bg-gray-800 dark:border-gray-700`}>
      {/* Header */}
      <div className="p-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={onNewConversation}
          className="flex-1 flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          <Plus size={16} />
          New Chat
        </button>
        {isMobile && onMobileClose ? (
          <button
            onClick={onMobileClose}
            className="ml-2 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700"
            title="Close"
          >
            <X size={18} />
          </button>
        ) : (
          <button
            onClick={onToggleCollapse}
            className="ml-2 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700"
            title="Collapse sidebar"
          >
            <ChevronLeft size={18} />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-8 pr-3 py-2 text-base sm:py-1.5 sm:text-sm bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
          />
        </div>
      </div>

      {/* Conversation list */}
      <div className={`flex-1 overflow-y-auto overscroll-contain ${isMobile ? 'pb-[env(safe-area-inset-bottom)]' : ''}`}>
        {conversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm dark:text-gray-400">
            No conversations yet
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm dark:text-gray-400">
            No matching conversations
          </div>
        ) : (
          groupOrder.map(group => {
            const groupConvs = groupedConversations[group];
            if (!groupConvs || groupConvs.length === 0) return null;

            return (
              <div key={group}>
                <div className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-widest dark:text-gray-500">
                  {group}
                </div>
                {groupConvs.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => onSelectConversation(conv)}
                    className={`w-full px-3 py-2.5 text-left flex items-start gap-2 hover:bg-gray-200 transition-colors duration-150 group dark:hover:bg-gray-700 ${
                      currentConversation?.id === conv.id ? 'bg-gray-200 dark:bg-gray-700' : ''
                    }`}
                  >
                    <MessageSquare size={16} className="text-gray-400 mt-0.5 flex-shrink-0 dark:text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate dark:text-gray-100">
                        {conv.title || 'New conversation'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
                        {new Date(conv.updated_at).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, conv.id)}
                      className={`p-1 rounded opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 ${
                        deleteConfirm === conv.id
                          ? 'bg-red-600 text-white opacity-100'
                          : 'text-gray-400 hover:text-red-500 hover:bg-gray-300 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-gray-600'
                      }`}
                      title={deleteConfirm === conv.id ? 'Click again to confirm' : 'Delete'}
                    >
                      <Trash2 size={14} />
                    </button>
                  </button>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
